/**
 * ApiKeysConfig — TZ 2026-09-23 Блоки 4/10: «Ключи».
 *
 * Key chips («кирпичики») — группировка ключей по провайдерам с цветной
 * плашкой провайдера, у каждого ключа — метки доступности тарифам
 * (эконом/оптимал/макс/супер, TZ Блок 5); попап редактирования ключа —
 * на i18n (были транслит-подписи) с чекбоксами тарифов.
 * Плюс кнопка «Проверить всё» (CheckAllPanel, TZ Блок 10).
 *
 * Было (Block 1): список ключей + форма добавления + Test per row.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Key,
  Plus,
  Trash2,
  Star,
  AlertCircle,
  CheckCircle2,
  Zap,
  Activity,
  Clock,
  Pencil,
} from 'lucide-react';

import {
  useCreateProvider,
  useDeleteProvider,
  useProviders,
  useTestProvider,
  useUpdateProvider,
  type AIProviderConfigCreate,
  type AIProviderConfig,
  type AIProviderConfigUpdate,
  type ProviderName,
  type ProviderStatus,
} from '@/hooks/useAdmin';
import { Modal } from '@/components/ui/Modal';
import { CheckAllPanel } from '@/components/admin/CheckAllPanel';
import { CarouselsConfig } from '@/components/admin/CarouselsConfig';
import {
  providerLabel,
  providerBadge,
  TIER_CHIP_CLASSES,
} from '@/components/admin/providerMeta';
import { cn } from '@/lib/utils';

const ALL_PROVIDERS: { value: ProviderName; label: string }[] = [
  { value: 'openai', label: 'OpenAI' },
  { value: 'anthropic', label: 'Anthropic' },
  { value: 'google', label: 'Google Gemini' },
  { value: 'openrouter', label: 'OpenRouter' },
  { value: 'perplexity', label: 'Perplexity' },
  { value: 'deepl', label: 'DeepL' },
  { value: 'serpapi', label: 'SerpAPI' },
];

const STATUS_COLORS: Record<ProviderStatus, string> = {
  active: 'bg-success-tint/10 text-success border-success-tint/20',
  exhausted: 'bg-warning-tint/10 text-warning border-warning-tint/20',
  error: 'bg-danger-tint/10 text-danger border-danger-tint/20',
  disabled: 'bg-surface-700/30 text-surface-500 border-surface-700',
};

const STATUS_LABELS: Record<ProviderStatus, string> = {
  active: 'system.providers.status.active',
  exhausted: 'system.providers.status.exhausted',
  error: 'system.providers.status.error',
  disabled: 'system.providers.status.disabled',
};

/** Тарифы (TZ Блок 5) для меток на ключах и каруселях. */
const TIER_KEYS = ['economy', 'optimal', 'max', 'super'] as const;

export function ApiKeysConfig() {
  const { t } = useTranslation();
  const { data: providers, isLoading } = useProviders();
  const create = useCreateProvider();
  const del = useDeleteProvider();

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<AIProviderConfigCreate>({
    provider: 'openai',
    display_name: '',
    api_key: '',
    is_default: false,
  });
  const [error, setError] = useState<string | null>(null);

  // Block 1: больше НЕ фильтруем — N ключей на провайдер это OK.
  // Показываем hint "already have N keys" под dropdown.

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.display_name.trim() || !form.api_key.trim()) {
      setError('Display name and API key are required');
      return;
    }
    try {
      await create.mutateAsync(form);
      setForm({ provider: form.provider, display_name: '', api_key: '', is_default: false });
      setShowForm(false);
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message;
      setError(msg ?? t('system.providers.errors.save_failed'));
    }
  };

  // P1.5 2026-07-28: native confirm() → <Modal>.
  const [pendingDelete, setPendingDelete] = useState<{ id: string; name: string } | null>(null);

  const requestDelete = (id: string, name: string) => {
    setPendingDelete({ id, name });
  };
  const confirmDelete = async () => {
    if (!pendingDelete) return;
    await del.mutateAsync(pendingDelete.id);
    setPendingDelete(null);
  };

  // Группировка «кирпичиками» по провайдерам (TZ Блок 4).
  const groups = (providers ?? [])
    .reduce<{ provider: string; keys: AIProviderConfig[] }[]>((acc, p) => {
      const g = acc.find((x) => x.provider === p.provider);
      if (g) g.keys.push(p);
      else acc.push({ provider: p.provider, keys: [p] });
      return acc;
    }, [])
    .sort((a, b) => providerLabel(a.provider).localeCompare(providerLabel(b.provider)));

  const keysForSelectedProvider = (providers ?? []).filter(
    (p) => p.provider === form.provider,
  ).length;

  return (
    <div className="space-y-4">
      {/* Заголовок раздела + «Проверить всё» (TZ Блок 10) */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-lg font-semibold">
          {t('system.providers.title', { defaultValue: 'Ключи' })}
        </h2>
        <CheckAllPanel />
      </div>

      {/* Ключи кирпичиками по провайдерам */}
      <div className="space-y-3">
        {groups.map((g) => (
          <div key={g.provider} className="card p-3 space-y-2">
            <div className="flex items-center gap-2">
              <span
                className={cn(
                  'text-xs px-2 py-0.5 rounded-md border font-semibold uppercase tracking-wider',
                  providerBadge(g.provider),
                )}
              >
                {providerLabel(g.provider)}
                <span className="ml-1 font-normal normal-case">· {g.keys.length}</span>
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
              {g.keys.map((p) => (
                <ProviderCard
                  key={p.id}
                  provider={p}
                  onDelete={() => requestDelete(p.id, p.display_name)}
                  deleting={del.isPending && del.variables === p.id}
                />
              ))}
            </div>
          </div>
        ))}
        {(providers ?? []).length === 0 && (
          <div className="text-sm text-surface-500 p-4 border border-dashed border-surface-700 rounded-xl text-center">
            {t('system.providers.no_keys')}
          </div>
        )}
      </div>

      {/* Карусели ключей — TZ Блок 5 FE */}
      <CarouselsConfig />

      {/* Кнопка «Добавить» */}
      {!showForm && (
        <button onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-2 w-fit">
          <Plus className="w-4 h-4" />
          {t('system.providers.add')}
        </button>
      )}

      {/* Форма добавления */}
      {showForm && (
        <form onSubmit={handleSubmit} className="card space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-surface-400 mb-1 block">{t('system.providers.provider')}</label>
              <select
                className="input w-full"
                value={form.provider}
                onChange={(e) => setForm({ ...form, provider: e.target.value as ProviderName })}
              >
                {ALL_PROVIDERS.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
              {keysForSelectedProvider > 0 && (
                <div className="text-xs text-warning mt-1">
                  {t('system.providers.already_have', {
                    count: keysForSelectedProvider,
                    defaultValue: 'У этого провайдера уже есть ключей: {{count}} — добавление расширяет пул.',
                  })}
                </div>
              )}
            </div>
            <div>
              <label className="text-xs text-surface-400 mb-1 block">{t('system.providers.display_name')}</label>
              <input
                className="input w-full"
                value={form.display_name}
                onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                placeholder="e.g. OpenAI Main"
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-surface-400 mb-1 block">{t('system.providers.api_key')}</label>
            <input
              type="password"
              className="input w-full font-mono"
              value={form.api_key}
              onChange={(e) => setForm({ ...form, api_key: e.target.value })}
              placeholder="sk-..."
              autoComplete="off"
            />
            <div className="text-xs text-surface-500 mt-1">{t('system.providers.encrypted_note')}</div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.is_default}
              onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
            />
            {t('system.providers.is_default')}
          </label>
          {error && (
            <div className="text-sm text-danger flex items-center gap-1">
              <AlertCircle className="w-4 h-4" /> {error}
            </div>
          )}
          <div className="flex gap-2">
            <button type="submit" disabled={create.isPending} className="btn-primary">
              {create.isPending ? t('system.providers.saving') : t('system.providers.save')}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                setError(null);
              }}
              className="btn-ghost"
            >
              {t('system.providers.cancel')}
            </button>
          </div>
        </form>
      )}

      {/* Delete confirm — P1.5 Modal */}
      <Modal
        open={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        title={t('system.providers.delete_confirm_title', { defaultValue: 'Удалить ключ?' })}
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setPendingDelete(null)}
              disabled={del.isPending}
            >
              {t('system.providers.cancel')}
            </button>
            <button
              type="button"
              className="btn-primary bg-danger-strong hover:bg-danger-hover"
              onClick={confirmDelete}
              disabled={del.isPending}
            >
              {del.isPending ? t('system.providers.deleting') : t('system.providers.delete')}
            </button>
          </>
        }
      >
        <p className="text-sm text-surface-300">
          {pendingDelete &&
            `Delete key "${pendingDelete.name}"? This cannot be undone.`}
        </p>
      </Modal>
    </div>
  );
}

function ProviderCard({
  provider,
  onDelete,
  deleting,
}: {
  provider: AIProviderConfig;
  onDelete: () => void;
  deleting: boolean;
}) {
  const { t } = useTranslation();
  const test = useTestProvider();
  const update = useUpdateProvider();
  const [testResult, setTestResult] = useState<{ ok: boolean; msg: string } | null>(null);
  // Edit modal — PATCH существующего ключа (display_name, api_key optional,
  // is_default, pinned_model, status). До этого правка была невозможна —
  // только create/delete, что плодило лишние строки (TZ 2026-08-01).
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState<AIProviderConfigUpdate & { api_key: string; allowed_tiers: string[] }>({
    display_name: provider.display_name,
    api_key: '',
    is_default: provider.is_default,
    pinned_model: provider.pinned_model,
    status: provider.status,
    allowed_tiers: provider.allowed_tiers ?? [...TIER_KEYS],
  });
  const [editError, setEditError] = useState<string | null>(null);

  const openEdit = () => {
    setEditForm({
      display_name: provider.display_name,
      api_key: '',
      is_default: provider.is_default,
      pinned_model: provider.pinned_model,
      status: provider.status,
      allowed_tiers: provider.allowed_tiers ?? [...TIER_KEYS],
    });
    setEditError(null);
    setEditOpen(true);
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEditError(null);
    const body: AIProviderConfigUpdate = {
      display_name: editForm.display_name?.trim() || provider.display_name,
      is_default: editForm.is_default,
      pinned_model: editForm.pinned_model ?? null,
      status: editForm.status,
      allowed_tiers: editForm.allowed_tiers,
    };
    // api_key опционален: пусто = не меняем; заполнено = ротация ключа.
    if (editForm.api_key && editForm.api_key.trim()) {
      body.api_key = editForm.api_key.trim();
    }
    try {
      await update.mutateAsync({ id: provider.id, body });
      setEditOpen(false);
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: { message?: string }; detail?: string } } })
        ?.response?.data;
      setEditError(
        (msg?.error?.message ?? msg?.detail) ?? t('system.providers.errors.save_failed'),
      );
    }
  };
  // Countdown — обновляем каждую минуту
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (provider.status !== 'exhausted' || !provider.exhausted_until) return;
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, [provider.status, provider.exhausted_until]);

  const handleTest = async () => {
    setTestResult(null);
    try {
      const r = await test.mutateAsync(provider.id);
      setTestResult({
        ok: r.success,
        msg: r.success
          ? `OK · ${r.latency_ms}ms${r.model_used ? ` · ${r.model_used}` : ''}`
          : `FAIL · ${r.http_status ?? '?'} · ${(r.error_message ?? 'unknown').slice(0, 80)}`,
      });
    } catch (err) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
        ?? (err as Error).message;
      setTestResult({ ok: false, msg: String(msg).slice(0, 120) });
    }
    // Авто-чистка результата через 6 сек
    setTimeout(() => setTestResult(null), 6_000);
  };

  // Health badge: показываем только если были реальные вызовы
  const showHealth = provider.success_count > 0 || provider.error_count > 0;
  // Countdown до reset exhausted
  const resetIn = provider.exhausted_until
    ? Math.max(0, Math.ceil((new Date(provider.exhausted_until).getTime() - now) / 60_000))
    : 0;

  return (
    <div className="card flex flex-col gap-3">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="w-10 h-10 rounded-xl bg-primary-500/10 flex items-center justify-center shrink-0">
            <Key className="w-5 h-5 text-primary-400" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="font-semibold truncate">{provider.display_name}</span>
              {provider.is_default && (
                <Star className="w-4 h-4 text-warning fill-warning shrink-0" />
              )}
            </div>
            <div className="text-xs text-surface-500 font-mono truncate">
              {provider.provider} · {provider.api_key_masked ?? '••••••••'}
              {provider.pinned_model ? ` · ${provider.pinned_model}` : ''}
            </div>
            {/* TZ Блок 5: метка доступности тарифам (эконом/оптимал/макс/супер) */}
            <div className="flex flex-wrap gap-1 mt-1">
              {TIER_KEYS.map((tk) => {
                const on =
                  (provider.allowed_tiers ?? []).length === 0 ||
                  (provider.allowed_tiers ?? []).includes(tk);
                const cls = TIER_CHIP_CLASSES[tk];
                return (
                  <span
                    key={tk}
                    className={cn(
                      'text-[10px] px-1 py-0.5 rounded border',
                      on
                        ? cls.on
                        : 'border-surface-700 text-surface-500',
                    )}
                    title={
                      on
                        ? t('system.providers.tiers.enabled', { defaultValue: 'Доступен' })
                        : t('system.providers.tiers.disabled', { defaultValue: 'Недоступен' })
                    }
                  >
                    {t(`system.tiers.${tk}`, { defaultValue: tk })}
                  </span>
                );
              })}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span
            className={cn(
              'text-xs px-2 py-0.5 rounded-md border whitespace-nowrap',
              STATUS_COLORS[provider.status],
            )}
          >
            {provider.status === 'active' && <CheckCircle2 className="w-3 h-3 inline mr-1" />}
            {provider.status === 'error' && <AlertCircle className="w-3 h-3 inline mr-1" />}
            {t(STATUS_LABELS[provider.status], { defaultValue: provider.status })}
          </span>
          <button
            onClick={openEdit}
            className="p-2 rounded-lg hover:bg-primary-500/10 text-surface-400 hover:text-primary-400 transition-colors"
            title={t("system.providers.edit_key")}
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            onClick={onDelete}
            disabled={deleting}
            className="p-2 rounded-lg hover:bg-danger-tint/10 text-surface-400 hover:text-danger transition-colors disabled:opacity-50"
            title={t("system.providers.delete")}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Block 1: badges row — health, countdown, test result */}
      <div className="flex flex-wrap items-center gap-2 text-xs">
        {showHealth && (
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-surface-800 text-surface-300"
            title={`success_count=${provider.success_count}, error_count=${provider.error_count}`}
          >
            <Activity className="w-3 h-3" />
            {provider.success_count} <CheckCircle2 className="w-3 h-3 text-success" />
            {' / '}
            {provider.error_count} <AlertCircle className="w-3 h-3 text-danger" />
          </span>
        )}
        {provider.status === 'exhausted' && provider.exhausted_until && (
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-warning-tint/10 text-warning border border-warning-tint/20"
            title={`Resets at ${provider.exhausted_until}`}
          >
            <Clock className="w-3 h-3" />
            Бесплатные лимиты закончились · Resets in {resetIn}m
          </span>
        )}
        <button
          onClick={handleTest}
          disabled={test.isPending}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-primary-500/10 text-primary-400 border border-primary-500/20 hover:bg-primary-500/20 disabled:opacity-50 transition-colors"
          title="Test this API key (real API call)"
        >
          <Zap className="w-3 h-3" />
          {test.isPending ? t('system.providers.testing') : t('system.providers.test')}
        </button>
        {testResult && (
          <span
            className={cn(
              'inline-flex items-center gap-1 px-2 py-0.5 rounded-md font-mono',
              testResult.ok
                ? 'bg-success-tint/10 text-success border border-success-tint/20'
                : 'bg-danger-tint/10 text-danger border border-danger-tint/20',
            )}
          >
            {testResult.ok ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
            {testResult.msg}
          </span>
        )}
      </div>

      {/* Edit modal — PATCH существующего ключа (update-not-create) */}
      <Modal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title={`${t('system.providers.edit_key')} · ${provider.display_name}`}
        footer={
          <>
            <button
              type="button"
              className="btn-ghost"
              onClick={() => setEditOpen(false)}
              disabled={update.isPending}
            >
              {t('system.providers.cancel')}
            </button>
            <button
              type="submit"
              form="edit-provider-form"
              disabled={update.isPending}
              className="btn-primary"
            >
              {update.isPending ? t('system.providers.saving') : t('system.providers.save')}
            </button>
          </>
        }
      >
        <form id="edit-provider-form" onSubmit={handleEditSubmit} className="space-y-3">
          <div>
            <label className="text-xs text-surface-400 mb-1 block">
              {t('system.providers.display_name', { defaultValue: 'Отображаемое имя' })}
            </label>
            <input
              className="input w-full"
              value={editForm.display_name ?? ''}
              onChange={(e) => setEditForm({ ...editForm, display_name: e.target.value })}
              placeholder="e.g. OpenAI Main"
            />
          </div>
          <div>
            <label className="text-xs text-surface-400 mb-1 block">
              {t('system.providers.api_key', { defaultValue: 'API ключ' })}
            </label>
            <input
              type="password"
              className="input w-full font-mono"
              value={editForm.api_key ?? ''}
              onChange={(e) => setEditForm({ ...editForm, api_key: e.target.value })}
              placeholder="Leave empty to keep current key"
              autoComplete="off"
            />
            <div className="text-xs text-surface-500 mt-1">
              {t('system.providers.rotate_note', {
                defaultValue: 'Заполняй только для смены ключа. Пусто = оставить как есть.',
              })}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-surface-400 mb-1 block">
                {t('system.providers.pinned_model', { defaultValue: 'Закреплённая модель' })}
              </label>
              <input
                className="input w-full font-mono"
                value={editForm.pinned_model ?? ''}
                onChange={(e) => setEditForm({ ...editForm, pinned_model: e.target.value || null })}
                placeholder="optional"
              />
            </div>
            <div>
              <label className="text-xs text-surface-400 mb-1 block">
                {t('system.providers.status_label', { defaultValue: 'Статус' })}
              </label>
              <select
                className="input w-full"
                value={editForm.status ?? provider.status}
                onChange={(e) => setEditForm({ ...editForm, status: e.target.value as ProviderStatus })}
              >
                {(['active', 'exhausted', 'error', 'disabled'] as ProviderStatus[]).map((st) => (
                  <option key={st} value={st}>
                    {t(`system.providers.status.${st}`, { defaultValue: st })}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {/* TZ Блок 5: чекбоксы «доступен тарифам» */}
          <div>
            <div className="text-xs text-surface-400 mb-1 block">
              {t('system.providers.tiers.label', { defaultValue: 'Доступен тарифам' })}
            </div>
            <div className="flex flex-wrap gap-2">
              {TIER_KEYS.map((tk) => {
                const checked = editForm.allowed_tiers.includes(tk);
                return (
                  <label key={tk} className="flex items-center gap-1.5 text-sm normal-case">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          allowed_tiers: e.target.checked
                            ? [...editForm.allowed_tiers, tk]
                            : editForm.allowed_tiers.filter((x) => x !== tk),
                        })
                      }
                    />
                    {t(`system.tiers.${tk}`, { defaultValue: tk })}
                  </label>
                );
              })}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={editForm.is_default ?? false}
              onChange={(e) => setEditForm({ ...editForm, is_default: e.target.checked })}
            />
            {t('system.providers.is_default', { defaultValue: 'По умолчанию для этого провайдера' })}
          </label>
          {editError && (
            <div className="text-sm text-danger flex items-center gap-1">
              <AlertCircle className="w-4 h-4" /> {editError}
            </div>
          )}
        </form>
      </Modal>
    </div>
  );
}
